/**
 * Retry utility for SDK operations
 * Implements exponential backoff with jitter, matching Go terminal retry behavior
 */

export interface RetryConfig {
  maxRetries: number;
  baseDelay: number; // milliseconds
  maxDelay: number; // milliseconds
  jitter: boolean;
  multiplier: number;
}

/**
 * Default retry configurations matching Go terminal implementation
 */
export const DefaultRetryConfigs = {
  // DefaultAuthConfig for authentication operations
  // Go: MaxRetries: 10, BaseDelay: 500ms, MaxDelay: 3s, Multiplier: 2.0
  auth: {
    maxRetries: 4,
    baseDelay: 500,
    maxDelay: 3000,
    jitter: true,
    multiplier: 2.0,
  } as RetryConfig,

  // DefaultHTTPConfig for HTTP requests
  http: {
    maxRetries: 3,
    baseDelay: 100,
    maxDelay: 5000,
    jitter: true,
    multiplier: 2.0,
  } as RetryConfig,

  // DefaultNonceConfig for nonce-related operations
  nonce: {
    maxRetries: 5,
    baseDelay: 200,
    maxDelay: 2000,
    jitter: true,
    multiplier: 1.5,
  } as RetryConfig,
};

/**
 * Calculate delay with exponential backoff and optional jitter
 */
function calculateDelay(config: RetryConfig, attempt: number): number {
  // Exponential backoff: baseDelay * (multiplier ^ attempt)
  let delay = config.baseDelay * Math.pow(config.multiplier, attempt);

  // Cap at max delay
  if (delay > config.maxDelay) {
    delay = config.maxDelay;
  }

  // Add jitter if enabled (up to 25% of delay)
  if (config.jitter) {
    const jitter = delay * 0.25 * Math.random();
    delay += jitter;
  }

  return Math.floor(delay);
}

/**
 * Sleep for specified milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Execute a function with retry logic
 * 
 * @param config - Retry configuration
 * @param fn - Function to execute (should throw/return error on failure)
 * @param shouldRetry - Function to determine if error should be retried (can be async)
 * @returns Promise that resolves on success or rejects after all retries exhausted
 */
export async function executeWithRetry<T>(
  config: RetryConfig,
  fn: () => Promise<T>,
  shouldRetry: (error: any) => boolean | Promise<boolean> = () => true
): Promise<T> {
  let lastError: any;

  for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
    try {
      // Execute the function
      const result = await fn();
      return result; // Success
    } catch (error: any) {
      lastError = error;

      // Check if we should retry this error (support async callbacks)
      const shouldRetryResult = await Promise.resolve(shouldRetry(error));
      if (!shouldRetryResult) {
        throw error;
      }

      // Don't wait after the last attempt
      if (attempt >= config.maxRetries) {
        break;
      }

      // Calculate delay with exponential backoff
      const delay = calculateDelay(config, attempt);

      // Wait for the delay
      await sleep(delay);
    }
  }

  // All retries exhausted
  throw new Error(
    `Operation failed after ${config.maxRetries + 1} attempts: ${lastError?.message || lastError}`
  );
}
